/**
 * Nombre de cada menú y módulo en el idioma de la persona.
 * El menú llega de la base de la empresa (tabla `menu` y `module`); aquí se traduce por su ruta
 * y, si no se conoce, se muestra el nombre guardado en la base.
 * Se aceptan la ruta vieja y la nueva del estándar (docs/estandar-nombres.md de ruttia-platform)
 * mientras cada módulo del refactor mueve sus rutas.
 */
import type { TextKey } from '../i18n';

const ROUTE_KEYS: Record<string, TextKey> = {
  'admin/users': 'menu.admin.users',
  'admin/offices': 'menu.admin.offices',
  'admin/sales-reps': 'menu.admin.sales-reps',
  'admin/clients': 'menu.admin.clients',
  'admin/routes': 'menu.admin.routes',
  'admin/loan-types': 'menu.admin.loan-types',
  'admin/expense-types': 'menu.admin.expense-types',
  'admin/banks': 'menu.admin.banks',
  'admin/devices': 'menu.admin.devices',
  'admin/alerts': 'menu.admin.alerts',
  'admin/route-defaults': 'menu.admin.route-defaults',
  'admin/storage': 'menu.admin.storage',
  'admin/permissions': 'menu.admin.permissions',
  'loans/payments': 'menu.loans.payments',
  'loans/cash-box-expenses/new': 'menu.loans.cash-box-expenses',
  'loans/expenses/new': 'menu.loans.expenses',
  'reports/payments-by-date': 'menu.reports.payments-by-date',
  'loans/route-devices': 'menu.loans.route-devices',
  'loans/payment-corrections': 'menu.loans.payment-corrections',
  'loans/correction-approvals': 'menu.loans.correction-approvals',
  'reports/cancellations': 'menu.reports.cancellations',
  'reports/expenses': 'menu.reports.expenses',
  'reports/client-history': 'menu.reports.client-history',
  'reports/loans-by-date': 'menu.reports.loans-by-date',
  'reports/balances': 'menu.reports.balances',
  'reports/cash-differences': 'menu.reports.cash-differences',
  'reports/daily-summary': 'menu.reports.daily-summary',
  'reports/discounts': 'menu.reports.discounts',
  'reports/app-sync': 'menu.reports.app-sync',
  'reports/deleted-records': 'menu.reports.deleted-records',
  'reports/sales-rep-risk': 'menu.reports.sales-rep-risk',
  'reports/consolidated-day': 'menu.reports.consolidated-day',
  'reports/cash-boxes': 'menu.reports.cash-boxes',
  'reports/cash-movements': 'menu.reports.cash-movements',
  'reports/refinancing': 'menu.reports.refinancing',
  'reports/late-loans': 'menu.reports.late-loans',
  'processes/visit-order': 'menu.processes.visit-order',
  'processes/route-tracking': 'menu.processes.route-tracking',
  'processes/route-settlement': 'menu.processes.route-settlement',
  'processes/export': 'menu.processes.export',
  'processes/import': 'menu.processes.import',
  'processes/imports': 'menu.processes.imports',
  'processes/loan-transfers': 'menu.processes.loan-transfers',
  'processes/sync-audit': 'menu.processes.sync-audit',
  'processes/app-notices': 'menu.processes.app-notices',
  'inventory/products': 'menu.inventory.products',
  'inventory/receipts': 'menu.inventory.receipts',
  'inventory/transfers': 'menu.inventory.transfers',
  'inventory/write-offs': 'menu.inventory.write-offs',
  'inventory/returns': 'menu.inventory.returns',
  'inventory/stock': 'menu.inventory.stock',
};

const MODULE_KEYS: Record<string, TextKey> = {
  administrar: 'module.admin',
  prestamos: 'module.loans',
  informes: 'module.reports',
  reportes: 'module.consolidated',
  procesos: 'module.processes',
  inventario: 'module.inventory',
  admin: 'module.admin',
  loans: 'module.loans',
  reports: 'module.reports',
  consolidated: 'module.consolidated',
  processes: 'module.processes',
  inventory: 'module.inventory',
};

const clean = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

export const menuTextKey = (route: string): TextKey | undefined =>
  ROUTE_KEYS[route.replace(/^\/+/, '').replace(/\/+$/, '')];

export const moduleTextKey = (name: string): TextKey | undefined => MODULE_KEYS[clean(name || '')];
